import { createBrowserClient } from '@supabase/ssr';
import type { SupabaseClient } from '@supabase/supabase-js';

import type { Database } from '@/lib/database.types';
import { env } from '@/lib/env';

export type {
  Database,
  Tables,
  TablesInsert,
  TablesUpdate,
  Enums,
  CompositeTypes,
} from '@/lib/database.types';

// Both vars are non-optional in the client schema, so the previous
// presence check is now enforced at boot instead of on first client build.
function readEnv() {
  return {
    supabaseUrl: env.NEXT_PUBLIC_SUPABASE_URL,
    supabaseAnonKey: env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
  };
}

/**
 * Creates a Supabase client for use in browser (client) components.
 *
 * Uses the public anon key -- all queries go through RLS.
 * The @supabase/ssr library handles cookie-based session management
 * automatically in the browser environment.
 *
 * Not schema-typed -- prefer {@link createTypedClient} in new code. See the
 * note in `src/lib/supabase/server.ts` for why the default stays untyped.
 */
export function createClient() {
  const { supabaseUrl, supabaseAnonKey } = readEnv();

  return createBrowserClient(supabaseUrl, supabaseAnonKey);
}

/**
 * Schema-typed counterpart to {@link createClient}.
 *
 * Table names, column names, and row shapes are checked against
 * `src/lib/database.types.ts`.
 */
export function createTypedClient(): SupabaseClient<Database> {
  const { supabaseUrl, supabaseAnonKey } = readEnv();

  return createBrowserClient<Database>(supabaseUrl, supabaseAnonKey);
}
